import * as Sentry from '@sentry/react';
import type { Event as SentryEvent } from '@sentry/types';
import {
  classifyError,
  type ClassifiedError,
  type ClassifyErrorOptions,
} from 'utils/classifyError';

export type SentryErrorSource =
  | 'query'
  | 'boundary'
  | 'global-runtime'
  | 'global-unhandledrejection'
  | 'async';

type CaptureClassifiedExceptionOptions = ClassifyErrorOptions & {
  source: SentryErrorSource;
};

function setClassificationTags(
  setTag: (key: string, value: string) => void,
  classified: ClassifiedError,
  source?: SentryErrorSource
): void {
  setTag('error.expectation', classified.expectation);
  setTag('error.layer', classified.layer);

  if (classified.codeError) {
    setTag('error.code', classified.codeError);
  }

  if (classified.httpStatus !== undefined) {
    setTag('error.http_status', String(classified.httpStatus));
  }

  if (source) {
    setTag('error.source', source);
  }
}

export function applyClassificationTagsToEvent(
  event: SentryEvent,
  classified: ClassifiedError,
  source?: SentryErrorSource
): void {
  const tags = { ...(event.tags ?? {}) };
  setClassificationTags(
    (key, value) => {
      tags[key] = value;
    },
    classified,
    source
  );
  event.tags = tags;
}

export function enrichSentryEventFromException(
  event: SentryEvent,
  error: unknown,
  options?: ClassifyErrorOptions
): ClassifiedError {
  const classified = classifyError(error, options);
  const source = event.tags?.['error.source'] as SentryErrorSource | undefined;
  applyClassificationTagsToEvent(event, classified, source);
  return classified;
}

export function captureClassifiedException(
  error: unknown,
  options: CaptureClassifiedExceptionOptions
): ClassifiedError {
  const { source, ...classifyOptions } = options;
  const classified = classifyError(error, classifyOptions);

  if (!classified.shouldReport) {
    return classified;
  }

  Sentry.withScope((scope) => {
    setClassificationTags((key, value) => scope.setTag(key, value), classified, source);
    Sentry.captureException(error);
  });

  return classified;
}
